import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { IncentiveOffer, IncentiveOfferType } from 'modules/incentivesMarket/types';
import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';
import { SellSelection } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/types/SellUnitsSelection';
import { mapSellerPlansToSellPlans } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/utils/mapSellerPlansToSellPlans';

import IncentiveOfferSellUnitsPanel from './IncentiveOfferSellUnitsPanel';
import LegacyIncentiveOfferActionCard from './LegacyIncentiveOfferActionCard';

type IncentiveOfferActionCardProps = {
  offer: IncentiveOffer;
  availableShares: number;
  sellerOfferCap?: {
    maxUnitsSellerCanOffer: number;
    effectiveMax: number;
  };
  sellerPlans?: IncentivesMarketPlanContext[];
  purchaseQuantity: number;
  isExpired: boolean;
  isOwnOffer: boolean;
  hasPartnerBuyer: boolean;
  isAdmin: boolean;
  isBeneficiaryView: boolean;
  /* eslint-disable-next-line no-unused-vars */
  onQuantityChange: (quantity: number) => void;
  /* eslint-disable-next-line no-unused-vars */
  onSellUnitsConfirm: (selection: SellSelection) => void;
  onBuyShares: () => void;
  onViewOtherOffers: () => void;
};

const IncentiveOfferActionCard: FC<IncentiveOfferActionCardProps> = ({
  offer,
  availableShares,
  sellerOfferCap,
  sellerPlans = [],
  purchaseQuantity,
  isExpired,
  isOwnOffer,
  hasPartnerBuyer,
  isAdmin,
  isBeneficiaryView,
  onQuantityChange,
  onSellUnitsConfirm,
  onBuyShares,
  onViewOtherOffers,
}) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === IncentiveOfferType.SELL;
  const isAdminOnlyView = isAdmin && !isBeneficiaryView;
  const showSellUnitsPanel =
    !isSellOffer && sellerPlans.length > 0 && !isAdminOnlyView;
  const showSellBreakdown =
    !isSellOffer && sellerPlans.length > 0 && !isAdminOnlyView;
  const hasCustomSellerLimits =
    !isSellOffer && offer.sellerParticipationLimits?.allowSellAll === false;

  const sellPlans = useMemo(
    () =>
      showSellUnitsPanel
        ? mapSellerPlansToSellPlans({ offer, sellerPlans })
        : [],
    [showSellUnitsPanel, offer, sellerPlans]
  );

  const acceptOfferDescription = useMemo(() => {
    if (isOwnOffer) return t('OwnOfferDescription');
    if (isSellOffer) return t('IncentivesMarketAcceptOfferDescription');
    return t('IncentivesMarketAcceptSellOfferDescription');
  }, [isOwnOffer, isSellOffer, t]);

  const mustBuyAll = !offer.isPartialAmountEnabled;

  const minPurchaseQuantity = useMemo(() => {
    if (mustBuyAll) return availableShares;
    return Math.max(
      1,
      Math.min(offer.minUnitsPerAcceptance ?? 1, availableShares)
    );
  }, [availableShares, mustBuyAll, offer.minUnitsPerAcceptance]);

  if (!isExpired && isAdminOnlyView && !isSellOffer) {
    return null;
  }

  if (isExpired) {
    return (
      <div
        className="card card-bordered"
        style={{ position: 'sticky', top: '20px' }}
      >
        <div className="card-inner text-center py-4">
          <p className="text-soft mb-3">{t('ExpiredOfferActionDescription')}</p>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onViewOtherOffers}
          >
            {t('ViewOtherOffers')}
          </button>
        </div>
      </div>
    );
  }

  if (showSellUnitsPanel) {
    return (
      <IncentiveOfferSellUnitsPanel
        plans={sellPlans}
        isDisabled={isOwnOffer || !hasPartnerBuyer}
        mustSellAll={mustBuyAll}
        minUnits={minPurchaseQuantity}
        maxTotalUnits={availableShares}
        onConfirm={onSellUnitsConfirm}
        onViewOtherOffers={onViewOtherOffers}
      />
    );
  }

  return (
    <LegacyIncentiveOfferActionCard
      offer={offer}
      availableShares={availableShares}
      sellerOfferCap={sellerOfferCap}
      sellerPlans={sellerPlans}
      purchaseQuantity={purchaseQuantity}
      isSellOffer={isSellOffer}
      showSellBreakdown={showSellBreakdown}
      hasCustomSellerLimits={hasCustomSellerLimits}
      acceptOfferDescription={acceptOfferDescription}
      mustBuyAll={mustBuyAll}
      minPurchaseQuantity={minPurchaseQuantity}
      isOwnOffer={isOwnOffer}
      hasPartnerBuyer={hasPartnerBuyer}
      onQuantityChange={onQuantityChange}
      onBuyShares={onBuyShares}
      onViewOtherOffers={onViewOtherOffers}
    />
  );
};

export default IncentiveOfferActionCard;
